/**
 * ============================================================================
 * ADMIN SECURITY PAGE
 * ============================================================================
 * Admin security management: hero, stats bento, pill tabs, themed content.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../providers';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import { useSecurity } from '../../hooks/useSecurity';
import { useTwoFactor } from '../../hooks/useTwoFactor';
import AdminLayout from '../../components/admin/AdminLayout';
import Input from '../../components/common/Input';
import Spinner from '../../components/common/Spinner';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';
import SecuritySettings from '../../components/security/SecuritySettings';
import SessionManager from '../../components/security/SessionManager';
import TwoFactorSetup from '../../components/security/TwoFactorSetup';
import SecurityActivity from '../../components/security/SecurityActivity';
import PasswordStrength from '../../components/security/PasswordStrength';
import '../../styles/landing.css';
import '../../styles/animations.css';
import '../../styles/global.css';

/**
 * Admin Security Component
 * @returns {JSX.Element}
 */
const AdminSecurity = () => {
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated } = useAuthContext();
  const { showError, showSuccess } = useToast();
  const { formatDate, language } = useI18n();
  const {
    securitySettings,
    activeSessions,
    securityEvents,
    loadSecuritySettings,
    loadActiveSessions,
    loadSecurityEvents,
    updateSecuritySettings,
    loading: securityLoading,
  } = useSecurity();
  const {
    status: twoFactorStatus,
    loadStatus: load2FAStatus,
    enable2FA,
    disable2FA,
    loading: twoFactorLoading,
  } = useTwoFactor();

  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  // Check if user is admin
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (authUser?.role !== 'admin' && authUser?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
  }, [isAuthenticated, authUser, navigate]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setStatsInView(true));
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await Promise.all([
          loadSecuritySettings(),
          loadActiveSessions(),
          loadSecurityEvents({ limit: 10 }),
          load2FAStatus().catch(() => {}),
        ]);
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement des données de sécurité' : 'Failed to load security data')
        );
        console.error('Load security data error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated && (authUser?.role === 'admin' || authUser?.role === 'super_admin')) {
      loadData();
    }
  }, [isAuthenticated, authUser, loadSecuritySettings, loadActiveSessions, loadSecurityEvents, load2FAStatus, showError, language]);

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }

    if (passwordStrength < 3) {
      showError(language === 'fr' ? 'Le mot de passe est trop faible' : 'Password is too weak');
      return;
    }

    try {
      // This would typically call an API to change password
      showSuccess(language === 'fr' ? 'Mot de passe modifié!' : 'Password changed!');
      setShowPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec de la modification' : 'Failed to change password'));
    }
  };

  const twoFactorEnabled = twoFactorStatus?.enabled === true;
  const handle2FAToggle = async () => {
    try {
      if (twoFactorEnabled) {
        // Disable 2FA would require password confirmation
        showError(language === 'fr' ? 'Utilisez le bouton Désactiver ci-dessous' : 'Use the Disable button below');
      } else {
        setShow2FAModal(true);
      }
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec de l\'action' : 'Failed to perform action'));
    }
  };

  const tabs = [
    {
      id: 'overview',
      label: language === 'fr' ? 'Vue d\'ensemble' : 'Overview',
      icon: '🛡️',
    },
    {
      id: 'settings',
      label: language === 'fr' ? 'Paramètres' : 'Settings',
      icon: '⚙️',
    },
    {
      id: 'sessions',
      label: language === 'fr' ? 'Sessions' : 'Sessions',
      icon: '🔐',
    },
    {
      id: 'activity',
      label: language === 'fr' ? 'Activité' : 'Activity',
      icon: '📊',
    },
  ];

  const securityStats = {
    activeSessions: activeSessions?.length || 0,
    recentEvents: securityEvents?.length || 0,
    twoFactorEnabled: twoFactorStatus?.enabled || false,
    lastLogin: authUser?.lastLogin || null,
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="transactions-wt transactions-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement de la sécurité...' : 'Loading security...'}
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const statItems = [
    {
      icon: '🔐',
      label: language === 'fr' ? 'Sessions actives' : 'Active Sessions',
      value: securityStats.activeSessions,
    },
    {
      icon: '🔒',
      label: '2FA',
      value:
        securityStats.twoFactorEnabled
          ? (language === 'fr' ? 'Activé' : 'Enabled')
          : (language === 'fr' ? 'Désactivé' : 'Disabled'),
    },
    {
      icon: '📊',
      label: language === 'fr' ? 'Événements récents' : 'Recent Events',
      value: securityStats.recentEvents,
    },
    {
      icon: '⏰',
      label: language === 'fr' ? 'Dernière connexion' : 'Last Login',
      value: securityStats.lastLogin ? formatDate(securityStats.lastLogin) : 'N/A',
    },
  ];

  return (
    <AdminLayout>
      <div className="transactions-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />
          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              🛡️ {language === 'fr' ? 'Sécurité Admin' : 'Admin Security'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Gérez la sécurité de votre compte administrateur'
                : 'Manage your administrator account security'}
            </p>
            <button
              type="button"
              className="dashboard-wt-btn-primary dashboard-wt-btn-large mt-6"
              onClick={() => navigate('/admin/dashboard')}
            >
              <span className="dashboard-wt-btn-icon">←</span>
              {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
            </button>
          </div>
        </section>

        {/* Stats bento */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
              {statItems.map((stat, i) => (
                <div
                  key={i}
                  className="dashboard-wt-stat-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="dashboard-wt-stat-icon">
                    <span>{stat.icon}</span>
                  </div>
                  <div className="dashboard-wt-stat-content">
                    <div className="dashboard-wt-stat-value">{stat.value}</div>
                    <div className="dashboard-wt-stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pill tabs */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className="dashboard-wt-card p-2 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
              <div className="flex gap-2 flex-wrap">
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTab(t.id)}
                    className={`flex items-center gap-2 py-3 px-4 rounded-lg font-medium transition-all flex-1 min-w-[120px] ${
                      activeTab === t.id
                        ? 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border border-[rgba(34,197,94,0.3)]'
                        : 'text-[#a3a3a3] hover:bg-[rgba(255,255,255,0.05)] border border-transparent'
                    }`}
                  >
                    <span>{t.icon}</span>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Tab content */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="dashboard-wt-card p-6 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
                  <h3 className="dashboard-wt-card-title mb-4 text-[#e5e5e5]">
                    {language === 'fr' ? 'Actions rapides' : 'Quick Actions'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="dashboard-wt-btn-primary py-3 px-6"
                      onClick={() => setShowPasswordModal(true)}
                    >
                      🔑 {language === 'fr' ? 'Changer le mot de passe' : 'Change Password'}
                    </button>
                    <button
                      type="button"
                      className={`py-3 px-6 rounded-lg font-medium transition-all ${
                        securityStats.twoFactorEnabled
                          ? 'bg-[rgba(239,68,68,0.15)] text-[#ef4444] border border-[rgba(239,68,68,0.3)] hover:bg-[rgba(239,68,68,0.25)]'
                          : 'dashboard-wt-btn-primary'
                      }`}
                      onClick={handle2FAToggle}
                    >
                      {securityStats.twoFactorEnabled ? '🔓' : '🔒'}{' '}
                      {securityStats.twoFactorEnabled
                        ? language === 'fr' ? 'Désactiver 2FA' : 'Disable 2FA'
                        : language === 'fr' ? 'Activer 2FA' : 'Enable 2FA'}
                    </button>
                  </div>
                </div>
                <div className="dashboard-wt-card p-6 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
                  <h3 className="dashboard-wt-card-title mb-4 text-[#e5e5e5]">
                    {language === 'fr' ? 'État de sécurité' : 'Security Status'}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(34,197,94,0.04)]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[rgba(34,197,94,0.2)] flex items-center justify-center text-xl">
                          🔒
                        </div>
                        <div>
                          <p className="font-bold text-[#e5e5e5]">
                            {language === 'fr' ? 'Authentification à deux facteurs' : 'Two-Factor Authentication'}
                          </p>
                          <p className="text-sm text-[#a3a3a3]">
                            {securityStats.twoFactorEnabled
                              ? (language === 'fr' ? 'Activé pour une sécurité renforcée' : 'Enabled for enhanced security')
                              : (language === 'fr' ? 'Non activé - Recommandé' : 'Not enabled - Recommended')}
                          </p>
                        </div>
                      </div>
                      <StatusBadge variant={securityStats.twoFactorEnabled ? 'success' : 'warning'}>
                        {securityStats.twoFactorEnabled
                          ? (language === 'fr' ? 'Activé' : 'Enabled')
                          : (language === 'fr' ? 'Désactivé' : 'Disabled')}
                      </StatusBadge>
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[rgba(59,130,246,0.2)] flex items-center justify-center text-xl">
                          🔐
                        </div>
                        <div>
                          <p className="font-bold text-[#e5e5e5]">
                            {language === 'fr' ? 'Sessions actives' : 'Active Sessions'}
                          </p>
                          <p className="text-sm text-[#a3a3a3]">
                            {securityStats.activeSessions}{' '}
                            {language === 'fr' ? 'appareil(s) connecté(s)' : 'device(s) connected'}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="dashboard-wt-btn-secondary py-2 px-4"
                        onClick={() => setActiveTab('sessions')}
                      >
                        {language === 'fr' ? 'Gérer' : 'Manage'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="dashboard-wt-card p-6 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
                <SecuritySettings />
              </div>
            )}

            {activeTab === 'sessions' && (
              <div className="dashboard-wt-card p-6 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
                <SessionManager />
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="dashboard-wt-card p-6 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
                <SecurityActivity />
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Change Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        }}
        title={language === 'fr' ? 'Changer le Mot de Passe' : 'Change Password'}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#e5e5e5] mb-2">
              {language === 'fr' ? 'Mot de Passe Actuel' : 'Current Password'}
            </label>
            <Input
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData((prev) => ({ ...prev, currentPassword: e.target.value }))}
              placeholder={language === 'fr' ? 'Entrez votre mot de passe actuel' : 'Enter your current password'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#e5e5e5] mb-2">
              {language === 'fr' ? 'Nouveau Mot de Passe' : 'New Password'}
            </label>
            <Input
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => {
                setPasswordData((prev) => ({ ...prev, newPassword: e.target.value }));
                // Calculate password strength
                const strength = calculatePasswordStrength(e.target.value);
                setPasswordStrength(strength);
              }}
              placeholder={language === 'fr' ? 'Entrez votre nouveau mot de passe' : 'Enter your new password'}
            />
            {passwordData.newPassword && (
              <div className="mt-2">
                <PasswordStrength password={passwordData.newPassword} />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-[#e5e5e5] mb-2">
              {language === 'fr' ? 'Confirmer le Mot de Passe' : 'Confirm Password'}
            </label>
            <Input
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              placeholder={language === 'fr' ? 'Confirmez votre nouveau mot de passe' : 'Confirm your new password'}
            />
            {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                {language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match'}
              </p>
            )}
          </div>
          <div className="flex gap-2 pt-4">
            <button
              type="button"
              className="dashboard-wt-btn-primary flex-1 py-3"
              onClick={handlePasswordChange}
              disabled={passwordStrength < 3 || passwordData.newPassword !== passwordData.confirmPassword}
            >
              🔑 {language === 'fr' ? 'Changer' : 'Change'}
            </button>
            <button
              type="button"
              className="dashboard-wt-btn-secondary flex-1 py-3"
              onClick={() => {
                setShowPasswordModal(false);
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              }}
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </button>
          </div>
        </div>
      </Modal>

      {/* 2FA Setup Modal */}
      <Modal
        isOpen={show2FAModal}
        onClose={() => setShow2FAModal(false)}
        title={language === 'fr' ? 'Configuration 2FA' : '2FA Setup'}
        size="lg"
      >
        <TwoFactorSetup
          onSuccess={() => {
            setShow2FAModal(false);
            load2FAStatus().catch(() => {});
            showSuccess(language === 'fr' ? '2FA activé avec succès!' : '2FA enabled successfully!');
          }}
        />
      </Modal>
    </AdminLayout>
  );
};

/**
 * Calculate password strength (0-4)
 */
const calculatePasswordStrength = (password) => {
  if (!password) return 0;
  let strength = 0;
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[^a-zA-Z\d]/.test(password)) strength++;
  return Math.min(strength, 4);
};

export default AdminSecurity;
